import { decode, encode } from "isomorphic-textencoder";

import { defined } from "@formant/core/common/defined";
import { logger } from "@formant/core/common/logger";
import { stats } from "@formant/core/common/stats";
import { duration } from "@formant/core/model/duration";
import { IRtcSignal } from "@formant/core/model/IRtcSignal";
import {
    rtcPingStreamType,
    rtcPongStreamType,
    rtcStreamControlStreamType,
} from "@formant/core/model/rtcStreamTypes";
import { Timeout } from "@formant/core/model/Timeout";
import { Timestamp } from "@formant/core/model/Timestamp";

import {
    latestReliableStreamConfig,
    latestReliableStreamLabel,
    latesTtlStreamLabel,
    latestTryOnceStreamConfig,
    latestTryOnceStreamLabel,
    latestTtlStreamConfig,
    reliableStreamConfig,
    reliableStreamLabel,
} from "./config/";
import { IHasPeerConnection } from "./IHasPeerConnection";
import { IRtcConnectionConfigurationV1 } from "./models-v1/IRtcConnectionConfigurationV1";
import { IIceMode } from "./models/IIceMode";
import { IRtcSendConfiguration } from "./models/IRtcSendConfiguration";
import { IRtcStreamMessage } from "./models/IRtcStreamMessage";
import { IRtcStreamPayload } from "./models/IRtcStreamPayload";
import { IStreamControl } from "./models/IStreamControl";
import { RtcSessionMetricsV1 } from "./RtcSessionMetricsV1";
import { createRtcStreamMessage } from "./utils/createRtcStreamMessage";
import { encodeStreamId } from "./utils/encodeStreamId";
import { isInactive } from "./utils/isInactive";
import { reifyZeroValues } from "./utils/reifyZeroValues";
import { streamIsEqual } from "./utils/streamIsEqual";

/*
 * A single connection on RtcClientV1.
 */
export class RtcConnectionV1 implements IHasPeerConnection {
    public iceMode?: IIceMode;

    private readonly connectTimeoutMs = 10 * duration.second;
    private readonly gatherIceTimeoutMs = 5 * duration.second;
    private readonly pingUpdateTimeoutMs = 2 * duration.second;

    // p2p RTC messages are sent over the following data channels
    private latestTtlStreamChannel?: RTCDataChannel;
    private reliableStreamChannel?: RTCDataChannel;
    private latestReliableStreamChannel?: RTCDataChannel;
    private latestTryOnceStreamChannel?: RTCDataChannel;

    private streamLatestTimestamp = new Map<string, Timestamp>();
    private pingUpdateTimeout: Timeout | undefined;
    private pingTimeMs: number | undefined;
    private lastMessageTimestamp: Timestamp | undefined;
    private connectTimeout: Timeout | undefined;
    private gatherIceTimeout: Timeout | undefined;
    private sessionMetrics: RtcSessionMetricsV1;
    private closeCalled = false;
    private connectCalled = false;
    private sentOffer = false;
    private receivedIceCandidate = false;

    constructor(
        public peerConnection: RTCPeerConnection,
        private config: IRtcConnectionConfigurationV1,
    ) {
        const { isOffer } = config;

        if (isOffer) {
            this.initializeChannels(peerConnection);
        } else {
            peerConnection.ondatachannel = _ => {
                switch (_.channel.label) {
                    case latesTtlStreamLabel:
                        this.latestTtlStreamChannel = _.channel;
                        break;
                    case reliableStreamLabel:
                        this.reliableStreamChannel = _.channel;
                        break;
                    case latestReliableStreamLabel:
                        this.latestReliableStreamChannel = _.channel;
                        break;
                    case latestTryOnceStreamLabel:
                        this.latestTryOnceStreamChannel = _.channel;
                        break;
                    default:
                        // We still set up channels we don't recognize.
                        // However, we ignore messages received on them.
                        // This is to preserve backwards compat when new channels are added.
                        // Otherwise, the connection status would be compromised on a new client
                        // connecting to an old client when one of the new client's channels never connected.
                        break;
                }
                this.setupChannel(_.channel);
            };
        }

        this.sessionMetrics = new RtcSessionMetricsV1(
            this.getSessionId(),
            this,
        );
    }

    public async connect() {
        if (this.connectCalled) {
            return;
        }
        this.connectCalled = true;

        this.pingUpdateTimeout = setInterval(() => {
            const { reliableStreamChannel } = this;
            if (!reliableStreamChannel) {
                return;
            }
            this.sendSystemMessage(reliableStreamChannel, {
                type: rtcPingStreamType,
            });
        }, this.pingUpdateTimeoutMs);

        this.connectTimeout = setTimeout(async () => {
            if (!this.isReady()) {
                logger.warn("RTC connect timed out, closing connection");
                await this.close();
            }
        }, this.connectTimeoutMs);

        const {
            peerConnection,
            config: { isOffer, sessionId },
        } = this;

        if (isOffer) {
            // Calling setLocalDescription triggers ICE gathering.
            // We don't send this offer signal. Instead, we wait for ICE gathering
            // to complete before re-generating and sending an offer (which has the candidates).
            const offer = await peerConnection.createOffer();
            await peerConnection.setLocalDescription(offer);
        }

        if (isOffer) {
            this.gatherIceTimeout = setTimeout(async () => {
                if (this.sentOffer) {
                    return;
                }
                logger.warn("ICE gathering timed out");
                if (this.receivedIceCandidate) {
                    logger.warn(
                        "Attempting to connect with partial ICE candidate pool",
                    );
                    await this.sendOffer();
                } else {
                    await this.close();
                }
            }, this.gatherIceTimeoutMs);

            peerConnection.onicecandidate = async event => {
                if (event.candidate) {
                    this.receivedIceCandidate = true;
                } else {
                    // received null candidate, ice gathering complete
                    // for backward compat also send offer here
                    await this.sendOffer();
                }
            };

            peerConnection.onicegatheringstatechange = async () => {
                if (peerConnection.iceGatheringState === "complete") {
                    await this.sendOffer();
                }
            };

            peerConnection.onnegotiationneeded = () => {
                logger.debug("Negotiation needed", {
                    sessionId,
                });
            };
        }
    }

    public async handleSignal(signal: IRtcSignal) {
        const {
            peerConnection,
            config: { sessionId, sendSignal },
        } = this;
        const { description } = JSON.parse(signal.payload);

        logger.debug("Handling signal", {
            sessionId,
            description: JSON.stringify(description),
        });

        if (!description) {
            logger.warn(`Received non-SDP signal: ${JSON.stringify(signal)}`);
            return;
        }

        if (
            peerConnection.signalingState === "stable" &&
            peerConnection.connectionState === "connected"
        ) {
            logger.warn(
                `Received SDP signal when signaling is stable and connected: ${JSON.stringify(
                    signal,
                )}`,
            );
            return;
        }

        if (peerConnection.remoteDescription) {
            logger.warn(
                `Received SDP signal during negotiation when remote description is already set: ${JSON.stringify(
                    signal,
                )}`,
            );
            return;
        }

        if (description.type === "offer") {
            await peerConnection.setRemoteDescription(description);
            const answer = await peerConnection.createAnswer();
            await peerConnection.setLocalDescription(answer);
            // The answer must reply to every offer description.
            await sendSignal({
                payload: JSON.stringify({
                    description: answer,
                }),
            });
        } else if (description.type === "answer") {
            await peerConnection.setRemoteDescription(description);
        }
    }

    /**
     * Sends a message to the remote peer.
     */
    public send(message: IRtcStreamMessage, config: IRtcSendConfiguration) {
        // All streams are enabled by default coming from the typescript client.

        const configuredChannel = this.getChannelFromLabel(config.channelLabel);

        if (configuredChannel) {
            this.sendOnChannel(configuredChannel, message);
            return;
        }

        logger.warn("Send called with unexpected channel label", {
            channelLabel: config.channelLabel,
        });
    }

    public controlRemoteStream(streamControl: IStreamControl) {
        this.sendSystemMessage(defined(this.reliableStreamChannel), {
            type: rtcStreamControlStreamType,
            streamControl,
        });
    }

    public isActive() {
        return new Set(["new", "connecting", "connected"]).has(
            this.peerConnection.connectionState,
        );
    }

    /**
     * Checks that the peerConnection is connected
     * and all data channels exist and are open
     */
    public isReady() {
        const {
            peerConnection,
            latestTtlStreamChannel,
            reliableStreamChannel,
            latestTryOnceStreamChannel,
        } = this;

        if (
            !reliableStreamChannel ||
            !latestTtlStreamChannel ||
            !latestTryOnceStreamChannel
        ) {
            return false;
        }

        return (
            peerConnection.connectionState === "connected" &&
            reliableStreamChannel.readyState === "open" &&
            latestTtlStreamChannel.readyState === "open" &&
            latestTryOnceStreamChannel.readyState === "open"
        );
    }

    /**
     * Checks that the peerConnection is closed and all
     * existing data channels are closed
     */
    public isClosed() {
        const {
            peerConnection,
            reliableStreamChannel,
            latestTryOnceStreamChannel,
            latestTtlStreamChannel,
        } = this;

        return (
            peerConnection.connectionState === "closed" &&
            (reliableStreamChannel === undefined ||
                reliableStreamChannel.readyState === "closed") &&
            (latestTryOnceStreamChannel === undefined ||
                latestTryOnceStreamChannel.readyState === "closed") &&
            (latestTtlStreamChannel === undefined ||
                latestTtlStreamChannel.readyState === "closed")
        );
    }

    /**
     * Checks that either the peerConnection is inactive
     * or at least one of the the data channels exists and is inactive
     */
    public needsClosing() {
        const {
            peerConnection,
            latestTtlStreamChannel,
            reliableStreamChannel,
            latestTryOnceStreamChannel,
        } = this;

        if (this.isClosed()) {
            return false;
        }

        const dataChannelInactiveStates = new Set(["closing", "closed"]);
        const channelIsInactive = (_: RTCDataChannel | undefined) =>
            _ && dataChannelInactiveStates.has(_.readyState);

        return (
            isInactive(peerConnection) ||
            channelIsInactive(latestTtlStreamChannel) ||
            channelIsInactive(reliableStreamChannel) ||
            channelIsInactive(latestTryOnceStreamChannel)
        );
    }

    public async close() {
        if (this.closeCalled) {
            return;
        }
        this.closeCalled = true;

        await this.sessionMetrics.uploadMetrics();
        if (this.pingUpdateTimeout) {
            clearInterval(this.pingUpdateTimeout);
        }
        if (this.connectTimeout) {
            clearTimeout(this.connectTimeout);
        }
        if (this.gatherIceTimeout) {
            clearTimeout(this.gatherIceTimeout);
        }
        this.peerConnection.close();
    }

    public getPing() {
        return this.pingTimeMs;
    }

    public getLastMessageTimestamp() {
        return this.lastMessageTimestamp;
    }

    public getSessionId() {
        return this.config.sessionId;
    }

    public getRemotePeer() {
        return this.config.remotePeer;
    }

    private initializeChannels(peerConnection: RTCPeerConnection) {
        this.latestTtlStreamChannel = peerConnection.createDataChannel(
            latesTtlStreamLabel,
            latestTtlStreamConfig,
        );
        this.reliableStreamChannel = peerConnection.createDataChannel(
            reliableStreamLabel,
            reliableStreamConfig,
        );
        this.latestReliableStreamChannel = peerConnection.createDataChannel(
            latestReliableStreamLabel,
            latestReliableStreamConfig,
        );
        this.latestTryOnceStreamChannel = peerConnection.createDataChannel(
            latestTryOnceStreamLabel,
            latestTryOnceStreamConfig,
        );
        this.setupChannel(this.latestTtlStreamChannel);
        this.setupChannel(this.reliableStreamChannel);
        this.setupChannel(this.latestReliableStreamChannel);
        this.setupChannel(this.latestTryOnceStreamChannel);
    }

    private async sendOffer() {
        if (this.sentOffer) {
            return;
        }
        this.sentOffer = true;

        if (isInactive(this.peerConnection)) {
            return;
        }

        const {
            peerConnection,
            config: { sendSignal },
        } = this;

        // Offer description will contain all ICE candidates
        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);
        const signal = {
            payload: JSON.stringify({
                description: offer,
            }),
        };
        await sendSignal(signal);
    }

    private getChannelFromLabel(label: string) {
        switch (label) {
            case latesTtlStreamLabel:
                return this.latestTtlStreamChannel;
            case reliableStreamLabel:
                return this.reliableStreamChannel;
            case latestReliableStreamLabel:
                return this.latestReliableStreamChannel;
            case latestTryOnceStreamLabel:
                return this.latestTryOnceStreamChannel;
        }
    }

    private sendOnChannel(channel: RTCDataChannel, message: IRtcStreamMessage) {
        let encoded: Uint8Array;
        try {
            encoded = encode(JSON.stringify(message));
        } catch (error) {
            logger.warn("Failed to encode RTC message", { error });
            return;
        }

        try {
            // Create a new Uint8Array with a proper ArrayBuffer for WebRTC DataChannel.send()
            const sendData = new Uint8Array(encoded);
            channel.send(sendData);
            this.sessionMetrics.incrementMessageSent(message);
        } catch (error) {
            logger.warn("Failed to send message to channel", {
                error,
                channel: channel.label,
            });
        }
    }

    private channelNotRecognized(channel: RTCDataChannel) {
        return (
            channel !== this.latestTtlStreamChannel &&
            channel !== this.latestReliableStreamChannel &&
            channel !== this.latestTryOnceStreamChannel &&
            channel !== this.reliableStreamChannel
        );
    }

    private setupChannel(channel: RTCDataChannel) {
        channel.onmessage = event => {
            if (this.channelNotRecognized(channel)) {
                logger.warn("Received message on unrecognized data channel.");
                return;
            }

            let message: IRtcStreamMessage;
            try {
                message = JSON.parse(decode(event.data));
            } catch (error) {
                // We want to ignore all invalid messages from other peers.
                logger.warn("Received unparseable RTC message", {
                    error,
                    channel: channel.label,
                });
                return;
            }

            this.lastMessageTimestamp = new Date().getTime();

            // Sets values that were ommitted by the JSON marshal/unmarshal process to zero
            message = reifyZeroValues(message);

            this.sessionMetrics.incrementMessageReceived(message);
            if (this.handleSystemMessage(channel, message)) {
                return;
            }

            if (!this.hasCapabilities(message)) {
                logger.warn(
                    "Received RTC message that was not within the capability scope of the connection.",
                );
                return;
            }

            const isLatestChannel =
                channel === this.latestTryOnceStreamChannel ||
                channel === this.latestTtlStreamChannel ||
                channel === this.latestReliableStreamChannel;
            if (isLatestChannel && !this.isLatestMessage(message)) {
                return;
            }

            this.config.receive(this.config.remotePeer.id, message);
        };

        channel.onerror = (event: any) => {
            logger.warn("Channel error", {
                error: event.error,
                sessionId: this.getSessionId(),
                channelLabel: channel.label,
            });
        };

        channel.onopen = () => {
            logger.debug("Channel opened", {
                sessionId: this.getSessionId(),
                channelLabel: channel.label,
            });
        };

        channel.onclose = () => {
            const meta = {
                bufferedAmount: channel.bufferedAmount,
                sessionId: this.getSessionId(),
                channelLabel: channel.label,
            };
            if (this.closeCalled === false) {
                logger.debug("Unexpected channel closed", meta);
            } else {
                logger.debug("Channel closed", meta);
            }
        };
    }

    /**
     * Handles system messages (e.g., ping/pong).
     * Returns true if the message is a system message.
     */
    private handleSystemMessage(
        channel: RTCDataChannel,
        message: IRtcStreamMessage,
    ): boolean {
        const type = message.header.stream.streamType;

        switch (type) {
            case rtcPingStreamType:
                this.sendSystemMessage(channel, {
                    type: rtcPongStreamType,
                    timestamp: defined(message.payload.ping),
                });
                return true;
            case rtcPongStreamType: {
                const pingTime =
                    new Date().getTime() - defined(message.payload.pong);
                this.pingTimeMs = pingTime;
                stats.aggregate("rtc_ping_time", pingTime);
                return true;
            }
            case rtcStreamControlStreamType:
                // Not implemented; all streams are enabled by default coming from the typescript client.
                return true;
            default:
                return false;
        }
    }

    private sendSystemMessage(
        channel: RTCDataChannel,
        systemData:
            | { type: typeof rtcPingStreamType }
            | { type: typeof rtcPongStreamType; timestamp: Timestamp }
            | {
                  type: typeof rtcStreamControlStreamType;
                  streamControl: IStreamControl;
              },
    ) {
        if (channel.readyState !== "open") {
            return;
        }

        // The stream we will send the system message on
        const stream = {
            entityId: defined(
                this.config.localPeer.deviceId || this.config.localPeer.userId,
            ),
            streamName: `$.${systemData.type}`,
            streamType: systemData.type,
        };

        // The payload of our system message
        let payload: IRtcStreamPayload;
        switch (systemData.type) {
            case rtcPingStreamType:
                payload = { ping: new Date().getTime() };
                break;
            case rtcPongStreamType:
                payload = { pong: systemData.timestamp };
                break;
            case rtcStreamControlStreamType:
                payload = { streamControl: systemData.streamControl };
                break;
        }

        this.sendOnChannel(channel, createRtcStreamMessage(stream, payload));
    }

    private hasCapabilities(message: IRtcStreamMessage) {
        // "Client-side message filtering"
        // We want to ignore all messages outside of the capability scope of the session.

        // The capability scope of a session is the union of the capability set
        // of the local and remote peers.

        // The goal of session-scoped capabilities and client-side filtering
        // is to build a robust permissions system where we can
        // (with the help of the trusted signaling API)
        // set up the capability scope of a p2p connection between two peers
        // that do not have full access to eachother's capabilities

        const { localPeer, remotePeer } = this.config;

        const localStreams = localPeer.capabilitySet.streaming?.streams;
        const localPeerCanReceiveMessage =
            localStreams &&
            localStreams.some(
                _ =>
                    _.receive && streamIsEqual(_.stream, message.header.stream),
            );

        const remoteStreams = remotePeer.capabilitySet.streaming?.streams;
        const remotePeerCanSendMessage =
            remoteStreams &&
            remoteStreams.some(
                _ => _.send && streamIsEqual(_.stream, message.header.stream),
            );

        return localPeerCanReceiveMessage || remotePeerCanSendMessage;
    }

    private isLatestMessage(message: IRtcStreamMessage) {
        const streamId = encodeStreamId(message.header.stream);
        const latest = this.streamLatestTimestamp.get(streamId) || 0;
        const isLatest = latest <= message.header.created;

        if (isLatest) {
            this.streamLatestTimestamp.set(streamId, message.header.created);
        }

        return isLatest;
    }
}
